<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

import BaseLoader from '@/components/Base/BaseLoader.vue'
import BaseAnimation from '@/components/Base/BaseAnimation.vue'
import BaseReward from '@/components/Base/BaseReward.vue'
import ErrorModal from '@/components/ErrorModal.vue'

import GameContestNotFound from '@/views/GameContestNotFound.vue'
import ContestGameWrapper from '@/components/ContestGame/Wrapper.vue'
import ErrorText from '@/components/ErrorText.vue'
import FortuneWheel from '@/Old/Index.vue'

import { clientApi } from '@/api/config'

import { useLocalStorageAvailability } from '@/composables/localStorage'

import useMeta from '@/composables/meta'
import { setRootCss } from '@/utils/root'

import { useGameContest } from '@/store'

import type { Merchant } from '@/types/index'

const props = defineProps<{
  id: string
  storeSlug: string
}>()

const { setMetaDescriptionAndFavicon, setNavigatorColor } = useMeta()
const { getMerchant, getGameContest } = useGameContest()

const tokenIsInvalid = ref(false)
const isNotFound = ref(false)

const store = useGameContest()
const { gameContest, contestReward, contestGameRewards, gameContestColor, locale } =
  storeToRefs(store)
const { setContestReward } = store

const merchant = computed<Merchant>(() => gameContest.value.merchant)

await Promise.all([getMerchant(), getGameContest(props.id, props.storeSlug)]).catch((err) => {
  if (err?.response?.status === 404) {
    isNotFound.value = true
  }
  if (err?.response?.status === 401) {
    tokenIsInvalid.value = true
  }
})

const contestGameMechanic = computed(() => gameContest.value.mechanic || 'scratchCard')
const logo = computed(() => gameContest.value?.logoUrl || merchant.value?.media?.logo || '')
const webAppUrl = computed(() => `https://${store.merchant.webAppUrl}`)

onMounted(() => {
  if (store.gameContest.id && store.gameContest.title) {
    document.title = store.gameContest.title

    setMetaDescriptionAndFavicon(
      merchant.value?.media?.logo,
      merchant.value?.name,
      gameContest.value.backgroundColor,
    )
    setNavigatorColor(gameContest.value.backgroundColor)
    setRootCss(gameContestColor.value)
  }
})
</script>

<template>
  <div class="bg-white">
    <GameContestNotFound v-if="isNotFound" />
    <ErrorText v-else-if="tokenIsInvalid" :text="$t('errors.invalidToken')"> 🤕 </ErrorText>

    <template v-if="!isNotFound && !tokenIsInvalid">
      <FortuneWheel
        v-if="contestGameMechanic === 'fortuneWheel'"
        :id="props.id"
        :contest-color="gameContestColor"
        :contest-game="{
          ...gameContest,
          mechanic: contestGameMechanic,
        }"
        :contest-game-rewards="contestGameRewards"
        :contest-reward="contestReward"
        :locale="locale"
        :logo="logo"
        :store-slug="props.storeSlug"
        :merchant="merchant"
      />
      <ContestGameWrapper
        v-else
        :id="props.id"
        :use-local-storage-availability="useLocalStorageAvailability()"
        :set-contest-reward="setContestReward"
        :client-api="clientApi"
        :contest-color="gameContestColor"
        :contest-game="{
          ...gameContest,
          mechanic: contestGameMechanic,
        }"
        :contest-game-rewards="contestGameRewards"
        :contest-reward="contestReward"
        :web-app-url="webAppUrl"
        :locale="locale"
        :logo="logo"
        :store-slug="props.storeSlug"
      >
        <template #animation="{ launchFireWorks }">
          <BaseAnimation
            v-if="contestReward"
            ref="animation"
            :model-value="launchFireWorks"
            :contest-game="gameContest"
            :contest-color="gameContestColor"
            :contest-reward="contestReward"
          />
        </template>

        <template #reward="{ startRewardAnimation, appUrl }">
          <BaseReward
            v-if="startRewardAnimation"
            :submit-link="appUrl"
            :contest-color="gameContestColor"
            :game-contest="gameContest"
            :contest-reward="contestReward"
          />
        </template>

        <template #loader>
          <BaseLoader :contest-color="gameContestColor" :logo="logo" />
        </template>

        <template #error-modal="{ errorModal, errorText }">
          <ErrorModal
            :model-value="errorModal"
            :error-text="errorText"
            :locale="locale"
            :contest-game="gameContest"
            :contest-color="gameContestColor"
          />
        </template>
      </ContestGameWrapper>
    </template>
  </div>
</template>
